import { View, StyleSheet, TouchableOpacity, ScrollView, useWindowDimensions } from 'react-native'
import React, { useState } from 'react'
import { Theme, ThemeConsumer } from '@rneui/themed'

import ModernCartItem from './cartItem';
import ActionSection from './smartAction';
import CustomerNoteSection from './customerNotes';
import { ZenIcon, ZenText } from 'zen-core-elements';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import CommonService from '../../../shared/services/commonService';
import ParkAndRetrieveSale from './parkAndRetrieveSale';
import { clearCart } from '../../../redux/slices/newSellscreenSlice';
import EmptyCart from '../emptyCart';
import CustomerModal from './addCustomer';
import { useAppNavigation } from '../../../types/@react-navigation/navigation';
import { setTotalAmount } from '../../../redux/slices/newSellscreenPaymentSlice';
import DiscountMessages from '../discounts/discountMessages';


const NewSellscreenCheckout = ({ onPressDetails, isWrite }: { isWrite: boolean, onPressDetails: (id: number) => void }) => {
	const navigation = useAppNavigation()
	const { width } = useWindowDimensions()
	const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)
	const dispatch = useAppDispatch()
	const {
		summary,
		additionalCharge,
		tip,
		cartQuantity,
		cartProducts,
		sellScreenType
	} = useAppSelector((state) => state?.NewSellscreenSlice)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	// Amount to collect: the rounded total when the store rounds off sales, otherwise the exact grand total
	const payableTotal = summary?.[locationId]?.payableTotal ?? summary?.[locationId]?.grandTotal
	const roundOff = summary?.[locationId]?.roundOff || 0

	// --- COMPONENT: Totals Row ---
	const TotalsRow = ({ label, value = 0, isBold = false, color }: { label: string, value?: string | number, isBold?: boolean, color?: string }) => (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme);
				return (
					<View style={[styles.totalRow, isBold && styles.totalRowBold]}>
						<ZenText content={label} style={[styles.totalLabel, isBold && styles.textBold, { color: color || theme?.grey }]} />
						<ZenText content={CommonService?.currencyStandard(currencySymbol, value?.toString())} style={[styles.totalValue, isBold && styles.textBold, { color: color || theme?.lightDark }]} />
					</View>
				)
			}}
		</ThemeConsumer>
	)

	const handleClearCart = () => {
		dispatch(clearCart())
	}

	const handleCheckout = () => {
		dispatch(setTotalAmount({ total: payableTotal, productCount: cartProducts?.[locationId]?.length }))
		navigation.navigate("OrderSummary")
	}

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View style={[styles.rightPanel, width < 600 && { padding: 10 }]}>
						<View style={styles.topHeader}>
							<ParkAndRetrieveSale isWrite={isWrite} />
							<CustomerModal isWrite={isWrite} />
						</View>

						{
							cartProducts?.[locationId]?.length ? <>
								<ScrollView style={{ flex: 1 }}>
									<ModernCartItem onPressDetails={onPressDetails} isWrite={isWrite} />
									<View style={styles.footerContainer}>

										<ActionSection />
										{sellScreenType === "SELL" && <CustomerNoteSection />}

										{sellScreenType === "SELL" && <DiscountMessages />}
										<View style={styles.breakdownCard}>
											<TotalsRow label="SubTotal" value={summary?.[locationId]?.subTotal} />
											<TotalsRow label="Item Discounts (-)" value={summary?.[locationId]?.otherDiscount} />
											<TotalsRow label="Coupon Discount (-)" value={summary?.[locationId]?.couponDiscount} />
											<TotalsRow label="Additional Discount (-)" value={summary?.[locationId]?.additionalDiscount} />

											<View style={styles.divider} />

											<TotalsRow label="Discounted Price" value={summary?.[locationId]?.discountedPrice} />
											<TotalsRow label="Additional Charge" value={additionalCharge?.[locationId]?.amount} />
											<TotalsRow label="Tax" value={summary?.[locationId]?.tax} />
											<TotalsRow label="Tip" value={tip?.[locationId] || 0} />
											{roundOff ? <>
												<View style={styles.divider} />
												<TotalsRow label="Total" value={summary?.[locationId]?.grandTotal} isBold />
												<TotalsRow label={`Round Off (${roundOff > 0 ? "+" : "-"})`} value={Math.abs(roundOff).toFixed(2)} />
											</> : null}
										</View>

										{/* Grand Total & Destructive Action */}
										<View style={styles.grandTotalSection}>
											<View>
												<View style={styles?.grandTotalView}>
													<ZenText content={"Grand Total"} style={styles.grandTotalLabel} />
													<ZenText content={`(Item: ${cartProducts?.[locationId]?.length}, Qty: ${cartQuantity?.[locationId]})`} style={styles.itemCount} />
												</View>
												<ZenText content={CommonService?.currencyStandard(currencySymbol, payableTotal?.toString())} style={styles.grandTotalValue} />
											</View>
											{cartProducts?.[locationId]?.length && sellScreenType === "SELL" ? <TouchableOpacity style={styles.clearCartBtn} onPress={handleClearCart}>
												<ZenIcon name="delete-outline" type='material' color={theme?.red500} size={15} />
												<ZenText content={"Clear"} style={styles.clearCartText} />
											</TouchableOpacity> : null}
										</View>
									</View>
								</ScrollView>
								<View style={styles.cartFooter}>
									<View style={styles.row}>
										<ZenText content="Total" style={styles.totalLabel} />
										<ZenText content={CommonService?.currencyStandard(currencySymbol, payableTotal?.toString())} style={styles.totalValue} />
									</View>
									<TouchableOpacity style={styles.checkoutBtn} onPress={handleCheckout}>
										<ZenText content={sellScreenType === "SELL" ? "Checkout" : sellScreenType === "RETURN" ? "Refund" : "Exchange"} style={styles.checkoutBtnText} />
									</TouchableOpacity>
								</View>
							</> :
								<EmptyCart isWrite={isWrite} />
						}
					</View>
				)
			}}
		</ThemeConsumer>
	)
}


const style = (theme: Theme) => (
	StyleSheet.create({
		rightPanel: { flex: 1, backgroundColor: theme?.light, paddingHorizontal: 16, paddingBottom: 16, paddingTop: 15 },
		topHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 16, alignItems: "center" },

		clearCartBtn: {
			flexDirection: 'row',
			alignItems: 'center',
			paddingVertical: 10,
			paddingHorizontal: 16,
			backgroundColor: theme?.red50,
			borderRadius: 10,
			gap: 5,
			borderWidth: 1,
			borderColor: theme?.red10,
		},
		clearCartText: { color: theme?.red500, fontFamily: theme?.medium, fontSize: theme?.errorFontSize },

		footerContainer: {
			backgroundColor: '#FFFFFF',
		},
		breakdownCard: {
			backgroundColor: '#F8FAFC', // Slightly different shade to separate
			borderRadius: 16,
			padding: 16,
			marginBottom: 20,
		},

		// Grand Total
		grandTotalSection: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'flex-end',
			borderTopWidth: 2,
			borderColor: '#F1F5F9',
			paddingVertical: 16,
		},
		grandTotalLabel: {
			fontSize: theme?.tinyFontSize,
			color: theme?.grey,
			fontFamily: theme?.medium
		},
		itemCount: {
			fontSize: theme?.miniFontSize,
			fontFamily: theme?.medium,
			color: theme?.draggerIcon
		},
		grandTotalValue: {
			fontSize: theme?.bigFontSize,
			fontFamily: theme?.bold,
			color: theme?.lightDark,
			letterSpacing: -0.5,
		},
		cartFooter: { borderTopWidth: 2, borderTopColor: '#F1F5F9', paddingTop: 12, marginTop: 'auto' },
		totalValue: { color: theme?.lightDark, fontSize: theme?.smallerFontSize, fontFamily: theme?.bold },
		checkoutBtn: { backgroundColor: theme?.thickDark, borderRadius: 10, alignItems: 'center', justifyContent: 'center', paddingVertical: 12 },
		row: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 },
		checkoutBtnText: { fontFamily: theme?.bold, color: theme?.light, fontSize: theme?.smallFontSize },
		totalLabel: { color: theme?.lightDark, fontSize: theme?.smallerFontSize, fontFamily: theme?.medium },

		totalRow: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			marginBottom: 8,
		},
		totalRowBold: {
			marginTop: 4,
			marginBottom: 8,
		},
		textBold: {
			fontFamily: theme?.bold,
			fontSize: theme?.smallHeaderFontSize,
		},
		divider: {
			height: 1,
			backgroundColor: theme?.coolGray,
			marginVertical: 10,
		},
		grandTotalView: {
			flexDirection: "row",
			alignItems: "center",
			gap: 5
		}
	}))



export default NewSellscreenCheckout